if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection.querySelector('.product-info');
      this.productForm = this.productSection.querySelector('product-form');

      this.addEventListener('click', this.handleAddClick.bind(this));

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
        this.bindLaunchers();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
      if (this.launcherObserver) this.launcherObserver.disconnect();
      if (this.sizeObserver) this.sizeObserver.disconnect();
      clearTimeout(this.launcherTimer);
      document.body.classList.remove('sticky-atc-showing');
    }

    bindEvents() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.productForm && !theme.mediaMatches.md) {
            // Phones: show the bar whenever the buy box is off screen (below the fold as well as scrolled past)
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
          } else if (entry.target === this.productInfo && theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
          }
        });
      });

      if (this.productForm) observer.observe(this.productForm);
      if (this.productInfo) observer.observe(this.productInfo);

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    /**
     * Keeps the floating chat and rewards launchers clear of the bar. While the bar is on screen the body carries
     * `sticky-atc-showing` (the CSS lifts the Rewards launcher above the bar, Impulse Chat stacks on top of it)
     * and `--sticky-atc-h` holds the bar's height so the lift follows it if the text size changes.
     */
    bindLaunchers() {
      this.launcherObserver = new MutationObserver(() => this.syncLaunchers());
      this.launcherObserver.observe(this, { attributes: true, attributeFilter: ['class'] });
      this.launcherObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      if ('ResizeObserver' in window) {
        this.sizeObserver = new ResizeObserver(() => {
          document.body.style.setProperty('--sticky-atc-h', `${Math.ceil(this.getBoundingClientRect().height)}px`);
        });
        this.sizeObserver.observe(this);
      }

      this.syncLaunchers();
    }

    /**
     * The bar is showing unless it is scrolled out (buy box on screen) or hidden at the bottom of the page.
     */
    syncLaunchers() {
      const body = document.body;
      const showing =
        !this.classList.contains('sticky-atc-panel--out') && !body.classList.contains('scrolled-to-bottom');
      if (body.classList.contains('sticky-atc-showing') === showing) return;
      body.classList.toggle('sticky-atc-showing', showing);
      // Impulse Chat re-reads where the Rewards launcher is on a visualViewport resize; a style-sheet move gives it
      // no other signal, so without this it trails the move by up to 30 seconds. Rewards animates for 0.2s, so
      // wait for it to land, or Chat would stack on a half-way position.
      clearTimeout(this.launcherTimer);
      this.launcherTimer = setTimeout(() => {
        if (window.visualViewport) window.visualViewport.dispatchEvent(new Event('resize'));
      }, 300);
    }

    /**
     * Presses the real Add to cart button, so the backorder gate, warranty upsell and cart drawer behave
     * exactly as for a tap on the main button. Falls back to the link (scroll to the buy box).
     * @param {object} evt - Click event.
     */
    handleAddClick(evt) {
      if (!evt.target.closest('[data-sticky-atc-add]')) return;
      const addBtn = this.productForm && this.productForm.querySelector('[name="add"]');
      if (!addBtn || addBtn.disabled) return;
      evt.preventDefault();
      addBtn.click();
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
